import { useNavigate, useParams } from "react-router-dom";
import { FC, useState } from "react";
import { Button, Grid } from "antd";
import { useTranslation } from "react-i18next";
import { ExclamationIcon } from "src/assets/Icons";

import { HotelDynamicDetailsProvider, StepButtonsPortalProvider } from "./context";
import { buildInitialPrimaryGuestKey, GuestsInputType, INITIAL_STEPS_VALIDATION, PaymentState, ValidityState } from "../../constants";
import { useGetRateConditionsQuery } from "src/store/apis/Hotels";

import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import useRestoreHotelTimer from "../../hooks/useRestoreHotelTimer";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import HotelDetailsWeb from "./layouts/HotelDetails.web";
import HotelDetailsMob from "./layouts/HotelDetails.mob";
import { usePriceBreakdown } from "../../hooks/usePriceBreakdown";
import usePayment from "./Steps/Payment/hooks/usePayment";
import { useSelector } from "react-redux";
import { getUserDetails } from "src/store/slices/client";


const RateConditionsErrorModal: FC<{ isError: boolean; onRetry: () => void }> = ({ isError, onRetry }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchId } = useParams();

  const handleGoBack = () => navigate(`/hotel/${searchId}`);

  if (!isError) return null;

  return (
    <div className="fixed inset-0 w-full z-50 flex items-center justify-center bg-background-inverted/40">
      <div className="relative w-96 max-w-96 flex w-full flex-col gap-4 rounded-lg p-6 shadow-sm bg-background-default">
        <div className="mb-4 h-16 w-16">
          <ExclamationIcon className="h-full w-full rounded-xl p-3 bg-background-danger! text-font-danger!" />
        </div>
        <h2 className="mb-1 text-lg font-bold">
          <span className="text-font-danger!">{t("Hotels.common.rateConditionsFailedTitle")}</span>
        </h2>
        <p className="text-sm">
          <span className="text-font-subtle!">{t("Hotels.common.rateConditionsFailedSubtitle")}</span>
        </p>
        <div className="flex flex-col gap-2">
          <Button
            className="w-full! rounded-lg! px-4! py-4! h-auto! text-sm! font-semibold! border-none! shadow-none! text-font-inverted! bg-background-primary!"
            onClick={onRetry}
          >
            {t("Hotels.common.retry")}
          </Button>
          <Button
            className="w-full! rounded-lg! px-4! py-4! h-auto! text-sm! font-semibold! border-none! shadow-none! text-font-active! bg-background-subtle!"
            onClick={handleGoBack}
          >
            {t("Hotels.common.goBackToHotels")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const HotelDetailsLayout = withConditionalComponent(HotelDetailsWeb, HotelDetailsMob);

export default function HotelDetails() {
  const { hotelKey, hotelDetailsKey } = useParams();
  const hotelDataString = localStorage.getItem(`${hotelKey}`);
  const hotelRoomDataString = localStorage.getItem(`${hotelDetailsKey}`);
  if (!hotelDataString || !hotelRoomDataString) return;
  const hotelRoomData = JSON.parse(hotelRoomDataString);

  const { hotel, searchObject, numberOfNights, selectedBookingCode, pointsFactor } = hotelRoomData;
  const selectedRoom = hotel.rooms.find((room: any) => room.bookingCode === selectedBookingCode) ?? null;
  const sessionId = hotelRoomData.sessionId ?? "";
  useRestoreHotelTimer(hotelRoomData._session);

  const validityState = useState<ValidityState>(INITIAL_STEPS_VALIDATION);
  const guestsState = useState<GuestsInputType>({});
  const primaryGuestKeyState = useState<string | null>(() => buildInitialPrimaryGuestKey(searchObject.rooms));
  const policiesAcceptedState = useState(false);
  const price = usePriceBreakdown({
    hotel,
    selectedRoom,
    pointsFactor,
  });
  const { totalPointsBalance } = useSelector(getUserDetails);
  const payment = usePayment({
    conversionFactor: pointsFactor,
    priceInCash: price.totalPrice,
    priceInPoints: price.pointsEquivalent,
    userTotalPoints: totalPointsBalance,
  });

  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];

  const {
    data: rateConditionsData,
    isFetching: isRateConditionsFetching,
    isError: isRateConditionsError,
    refetch: refetchRateConditions,
  } = useGetRateConditionsQuery({ bookingCode: selectedRoom?.bookingCode ?? "" }, { skip: !selectedRoom?.bookingCode });

  return (
    <StepButtonsPortalProvider>
      <HotelDynamicDetailsProvider
        value={{
          validityState,
          guestsState,
          primaryGuestKeyState,
          policiesAcceptedState,
          hotel,
          payment,
          selectedRoom,
          searchObject,
          numberOfNights,
          pointsFactor: typeof pointsFactor === "number" ? pointsFactor : 1,
          sessionId,
          rateConditions: rateConditionsData?.rateConditions ?? [],
          rateConditionRoom: rateConditionsData?.rooms?.[0] ?? null,
          isRateConditionsFetching,
        }}
      >
          <RateConditionsErrorModal isError={isRateConditionsError} onRetry={refetchRateConditions} />
          <HotelDetailsLayout trigger={isLargeScreen} />
      </HotelDynamicDetailsProvider>
    </StepButtonsPortalProvider>
  );
}
